import React, { useCallback, useEffect, useState } from 'react';

import { JWTAuthorization } from '../../../utils/JWTHeader';
import FileUpload from '../FileUpload/FileUpload.module';
import { useFileUpload } from '../FileUpload/useFileUpload';
import { showToast } from '../Notification';
import Popup from '../Popup/Popup.module';
import Spinner from '../Spinner';

const MAX_FILE_SIZE = 5;

interface ReferentPresentationUploadProps {
  companyId: number;
  referentId: string;
  referentLabel: string;
  jwt: string;
  onUploadSuccess?: () => void;
  onDeleteSuccess?: () => void;
}

const ReferentPresentationUpload: React.FC<ReferentPresentationUploadProps> = ({
  companyId,
  referentId,
  referentLabel,
  jwt,
  onUploadSuccess,
  onDeleteSuccess,
}) => {
  const [referentPresentation, setReferentPresentation] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  const apiBase = `/api/companies/${companyId}/referents/${encodeURIComponent(referentId)}/report/settings/pdf-introduction`;

  const getPdfFetchUrl = useCallback(
    () => `${apiBase}?v=${Date.now()}`,
    [apiBase],
  );

  const fetchReferentPdf = useCallback(async() => {
    setIsLoading(true);
    try {
      const response = await fetch(getPdfFetchUrl(), {
        headers: { Authorization: JWTAuthorization(jwt) },
        cache: 'no-store',
      });
      if (response.ok) {
        const blob = await response.blob();
        const file = new File([blob], `presentation-${referentId}.pdf`, { type: 'application/pdf' });
        setReferentPresentation(file);
      } else {
        setReferentPresentation(null);
      }
    } catch (error) {
      console.error('Error fetching referent PDF:', error);
      setReferentPresentation(null);
    } finally {
      setIsLoading(false);
    }
  }, [getPdfFetchUrl, jwt, referentId]);

  useEffect(() => {
    if (companyId && jwt && referentId) {
      fetchReferentPdf();
    }
  }, [companyId, jwt, referentId, fetchReferentPdf]);

  const handleUpload = async(file: File): Promise<boolean> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(apiBase, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload referent presentation');
      }

      await fetchReferentPdf();
      showToast('success', `La présentation de ${referentLabel} a été mise à jour avec succès.`);
      onUploadSuccess?.();
      return true;
    } catch (error) {
      console.error('Error uploading referent presentation:', error);
      showToast('error', `Une erreur est survenue lors du téléchargement de la présentation de ${referentLabel}.`);
      setReferentPresentation(null);
      return false;
    }
  };

  const handleDelete = async() => {
    try {
      const response = await fetch(apiBase, {
        method: 'DELETE',
        headers: { Authorization: JWTAuthorization(jwt) },
      });

      if (!response.ok) {
        throw new Error('Failed to delete referent presentation');
      }

      setReferentPresentation(null);
      showToast('success', `La présentation de ${referentLabel} a été supprimée avec succès.`);
      await onDeleteSuccess?.();
    } catch (error) {
      console.error('Error deleting referent presentation:', error);
      showToast('error', `Une erreur est survenue lors de la suppression de la présentation de ${referentLabel}.`);
    }
  };

  const fileUpload = useFileUpload({
    onFileSelect: handleUpload,
    onFileDelete: handleDelete,
    acceptedFileTypes: '.pdf',
    maxFileSize: MAX_FILE_SIZE,
    currentFile: referentPresentation,
  });

  const handlePreviewPDF = async() => {
    try {
      const response = await fetch(getPdfFetchUrl(), {
        headers: { Authorization: JWTAuthorization(jwt) },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      showToast('error', 'Une erreur est survenue lors de la prévisualisation du PDF.');
    }
  };

  const closePdfPreview = () => {
    setIsPdfPreviewOpen(false);
    setPdfPreviewUrl(null);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h4 className="mb-2 font-semibold text-gray-800">{referentLabel}</h4>
      <p className="mb-4 text-sm text-slate-600">
        Présentation utilisée en tête des rapports des copropriétés associées à ce référent publique.
      </p>
      {isLoading ? (
        <Spinner aria-label={`Loading PDF for ${referentLabel}`} />
      ) : (
        <FileUpload
          onFileSelect={fileUpload.handleFiles}
          onFileDelete={fileUpload.removeFile}
          acceptedFileTypes=".pdf"
          maxFileSize={MAX_FILE_SIZE}
          currentFile={fileUpload.file}
        />
      )}
      {fileUpload.file && (
        <button
          onClick={handlePreviewPDF}
          className="mt-2 w-full rounded border-2 border-rgpd-blue bg-transparent px-2 py-1 font-semibold text-rgpd-blue hover:bg-rgpd-blue hover:text-white md:w-auto"
        >
          Prévisualiser le PDF
        </button>
      )}
      <Popup
        isOpen={isPdfPreviewOpen}
        onClose={closePdfPreview}
        title={`Présentation — ${referentLabel}`}
        width="90vw"
        height="90vh"
      >
        <div className="h-full w-full">
          <iframe
            src={pdfPreviewUrl}
            className="h-full w-full border-none"
            title="PDF Preview"
          />
        </div>
      </Popup>
    </div>
  );
};

export default ReferentPresentationUpload;
